<template>
  <AbstractSocialCard
    :id="post._id"
    :author="post.author"
    :social-icon="{
      name: 'fa-square-x-twitter',
      color: 'var(--clr-twitter)',
    }"
    :handle="handleTwitter"
    :reposts="SINumberFormatter(post.reposts)"
    :likes="SINumberFormatter(post.like)"
    :comments="SINumberFormatter(post.comment)"
    :impressions="SINumberFormatter(post.impression)"
    :created-at="post.created_at"
    :last-update="post.last_update"
    :source-url="post.permalink"
    :is-overview="isOverview"
  >
    <template v-if="retweet.isRetweet" #banner>
      <p class="retweet-banner title-small-regular">
        <i class="fa-solid fa-retweet retweet-banner__icon" />
        <span class="retweet-banner__text">
          <strong class="retweet-banner__name">{{ post.author.name }}</strong>
          a reposté
        </span>
      </p>
    </template>

    <template #content>
      <div :class="{ 'retweet-original': retweet.isRetweet }">
        <p
          v-if="retweet.isRetweet"
          class="retweet-original__author title-small-regular"
        >
          <i class="fa-brands fa-square-x-twitter" />
          <BaseLink
            v-if="originalHandle.url"
            :href="originalHandle.url"
            class="retweet-original__handle"
            >@{{ originalHandle.label }}</BaseLink
          >
          <span v-else>Publication d'origine</span>
        </p>

        <AbstractSocialCardContent :describe="tweetText" />

        <SourceLink
          v-for="url in tweetLinks"
          :key="url"
          :href="url"
          :label="url"
        />

        <p v-if="retweet.originalUrl" class="retweet-original__link">
          <SourceLink
            :href="retweet.originalUrl"
            label="Voir le post original"
          />
        </p>
      </div>
    </template>
  </AbstractSocialCard>
</template>

<script setup>
import { computed } from "vue";
import { SINumberFormatter } from "@follaw-fr/utils/helper.utils.js";
import BaseLink from "@follaw-fr/components/general/atoms/BaseLink.vue";
import SourceLink from "@follaw-fr/components/general/atoms/SourceLink.vue";
import AbstractSocialCard from "@follaw-fr/components/social-media//abstract/AbstractSocialCard.vue";
import AbstractSocialCardContent from "@follaw-fr/components/social-media//abstract/AbstractSocialCardContent.vue";
import { useHandle } from "@follaw-fr/composables/social-media/useHandle";
import { Platforms } from "@follaw-fr/utils/social360-platforms.js";
import { parseRetweet } from "@follaw-fr/utils/retweet.utils.js";

const props = defineProps({
  post: {
    type: Object,
    required: true,
  },
  isOverview: {
    type: Boolean,
    default: false,
  },
});

const handleTwitter = useHandle(
  props.post.author.x_information?.username,
  Platforms.TWITTER
);

const linksRegex = /(https:\/\/t.co\/[a-zA-Z0-9]{10})/g;

const retweet = computed(() => parseRetweet(props.post));
const originalHandle = computed(() =>
  useHandle(retweet.value.originalHandle, Platforms.TWITTER)
);
const tweetLinks = computed(() => retweet.value.text.match(linksRegex));
const tweetText = computed(() => retweet.value.text.replace(linksRegex, ""));
</script>

<style lang="scss" scoped>
.retweet-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 6px 12px;
  border-radius: 4px;
  background-color: var(--clr-g50);
  color: var(--clr-g500);

  &__icon {
    flex-shrink: 0;
    color: var(--clr-g400);
  }

  &__text {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__name {
    text-transform: capitalize;
  }
}

.retweet-original {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--clr-n4);
  border-left: 4px solid var(--clr-g400);
  border-radius: 8px;
  background-color: var(--clr-n1);
  overflow-wrap: anywhere;

  &__author {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: var(--clr-n7);
  }

  &__handle {
    font-weight: 600;
  }

  &__link {
    margin: 0;
  }
}
</style>
